import type { CritMods, ProjectileSpec } from "../models/ProjectileSpec.js";
import { rollCrit } from "../models/ProjectileSpec.js";
import { COMBAT, MODULE_BRICK_BALANCE, MAX_PROJECTILE_SPEED } from "../data/balance.js";
import type {
  GhostEmitter,
  NearestBrickLocator,
  Projectile,
  ProjectileDespawn,
} from "./Projectile.js";
import { clampProjectileSpeed } from "./projectileSweep.js";
import type { PlayfieldBounds } from "./bounceBounds.js";
import { spreadAngleOffsets } from "../systems/spreadAngleOffsets.js";
import { resolveFireCount } from "../systems/projectileCount.js";

/** 도(°) → 라디안 — Phaser.Math.DegToRad 대체(이 모듈을 Phaser-free로 유지해 node 단위테스트 가능). */
const DEG_TO_RAD = Math.PI / 180;
const DEFAULT_PROJECTILE_TEXTURE_KEY = "projectile_basic_white";

/** 크리티컬 모듈 강화 계수 — global.tsv SSoT(balance). 발사체마다 동일하므로 spawn 1회 구성해 재사용. */
const CRIT_MODS: CritMods = {
  explosionRadiusMul: MODULE_BRICK_BALANCE.critExplosionRadiusMul,
  pierceBonus: MODULE_BRICK_BALANCE.critPierceBonus,
  chainBonus: MODULE_BRICK_BALANCE.critChainBonus,
  splitBonus: MODULE_BRICK_BALANCE.critSplitBonus,
  greedMul: MODULE_BRICK_BALANCE.critGreedMul,
};

export interface FireOrigin {
  readonly x: number;
  readonly y: number;
}

/**
 * 발사체 공급원 — `acquire()`로 재사용(또는 신규) 발사체를 얻는다.
 * 런타임 구현은 `@yjsgames/object-pool`의 `ObjectPool<Projectile>`(BattlefieldRunner 소유), 테스트는 stub.
 * 풀 추상화에 의존해(구체 ObjectPool 아님) factory를 Phaser·풀 구현 무관하게 단위테스트한다.
 */
export interface ProjectileSource {
  acquire(): Projectile;
}

/**
 * 최종 ProjectileSpec으로 발사체(count발)를 생성한다 (지시문 §12·§13).
 * 발사 방향(baseAngle)은 호출자가 결정 — 포신 회전축 기준 aimBaseAngleRad(SSoT)를 주입받아, 회전하는 포신·
 * 조준선과 동일한 방향을 보장한다. count>1이면 baseAngle 기준 부채꼴. (Projectile의 homing in-flight 추적은 별도.)
 * 발사 위치(origin)는 포구 끝(muzzleTip) — 축이 아니라 포신 끝에서 시작·소멸 판정한다.
 * 크리티컬은 발사체마다 rollCrit(rng)로 독립 판정 — 크리티컬 발사체만 피해 배율 + 크기 강조.
 *
 * ⚠ 풀링: `new Projectile` 대신 `pool.acquire()` + `configure()`로 인스턴스를 재사용한다(GC churn 제거).
 */
export class ProjectileFactory {
  /**
   * @param pool      발사체 공급원(ObjectPool<Projectile>) — acquire로 재사용/신규 인스턴스.
   * @param spec      최종 발사체 설계도. count·speedMultiplier·critChance 등이 발사 수/속도/판정에 쓰인다.
   * @param origin    발사 위치(포구 끝 = muzzleTip(축, baseAngle, length)).
   * @param baseAngle 조준 기준 각도(rad) — 포신 회전축 기준 aimBaseAngleRad. 포신·조준선과 동일 SSoT.
   * @param locator   가장 가까운 벽돌 조회 — 발사체 homing in-flight 추적용(조준 각도는 baseAngle로 이미 결정).
   * @param bounds    벽 반사 경계(게임뷰 프레임 interior). 변형 없이 각 Projectile에 그대로 주입돼 반사·소멸 판정에 쓰인다.
   * @param emitGhost 잔상 고스트 방출 콜백 — 셸이 고스트 풀로 띄운다.
   * @param despawn   소멸 통지 콜백 — 셸이 발사체 풀로 반납한다.
   * @param rng       크리티컬 판정 난수(기본 Math.random) — 테스트 결정론 주입용.
   * @param textureKey 함선별 투사체 텍스처 키. 흰색 base에 런타임 element tint를 입힌다.
   */
  spawn(
    pool: ProjectileSource,
    spec: ProjectileSpec,
    origin: FireOrigin,
    baseAngle: number,
    locator: NearestBrickLocator,
    bounds: PlayfieldBounds,
    emitGhost: GhostEmitter,
    despawn: ProjectileDespawn,
    rng: () => number = Math.random,
    textureKey = DEFAULT_PROJECTILE_TEXTURE_KEY,
  ): Projectile[] {
    // 속도 상한 clamp — speedMultiplier(유물/모듈) 극단 누적 시 수치 안정(연속충돌 sweep과 별개 방어 레이어).
    const speed = clampProjectileSpeed(
      MODULE_BRICK_BALANCE.baseProjectileSpeed * spec.speedMultiplier,
      MAX_PROJECTILE_SPEED,
    );
    const n = resolveFireCount(spec.count); // SSoT — DPS 추정(combatStats)과 동일 floor+clamp

    const created: Projectile[] = [];
    const offsets = spreadAngleOffsets(n);
    for (let i = 0; i < n; i++) {
      const offsetDeg = offsets[i] * COMBAT.spreadAngleDeg;
      const angle = baseAngle + offsetDeg * DEG_TO_RAD;
      const { spec: shotSpec, isCrit } = rollCrit(spec, rng, CRIT_MODS);
      const projectile = pool.acquire();
      projectile.configure(origin.x, origin.y, shotSpec, locator, bounds, emitGhost, despawn, textureKey);
      projectile.isCrit = isCrit; // 충돌 시 critical! 표시·폭발 색 분기의 SSoT
      if (isCrit) projectile.setScale(1.4); // 크리티컬 시각 강조 (더 큰 발사체)
      projectile.launch(Math.cos(angle) * speed, Math.sin(angle) * speed);
      created.push(projectile);
    }
    return created;
  }
}
